import {getTestProps} from '@benchling/js-test-utils';
import classNames from 'classnames';
import $ from 'jquery';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import EditableComponent from 'notebook/mediocre/core-ui/EditableComponent';
// @ts-expect-error
import * as selutil from 'notebook/mediocre/selection/text/SelUtil';
import {INDENT_SIZE} from 'notebook/notes/lists/ListConstants';
import * as TableConstants from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {NotePartItem} from 'notebook/util/NoteUtilConstants';
import * as keyboard from 'util/Keyboard';
import {ElementClipboardEvent, ElementKeyboardEvent, ElementMouseEvent} from 'util/Types';

import {SelectionPartition} from '../EditorTypes';
import {Format} from '../formatted-text/FormattersConstants';
import {forwardSearchResultProps, MediocreSearchResultProps} from '../search/SearchResultProps';
import CaptionEditableItem, {CaptionEditableItemProps} from './CaptionEditableItem';

const CAPTION_MIN_WIDTH = 175;

function getContentAlignment(content: PreviewPluginContent | TablePluginContent): string | null | undefined {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'alignment' in content ? content.alignment : undefined;
}

function getContentItemWidth(content: PreviewPluginContent | TablePluginContent): number | undefined {
  // eslint-disable-next-line benchlint/no-in-operator
  return 'itemWidth' in content ? content.itemWidth : undefined;
}

export interface PreviewPluginContent {
  text: string;
  formats: Format | undefined;
  alignment: string | null;
  item: NotePartItem;
  item_id: string | number | null;
  placeholder: boolean;
  itemWidth: number;
}

export interface TablePluginContent {
  text: string;
  formats: Format | undefined;
  table: TableConstants.Table;
}

// If tableMode is true, we render the CaptionEditable slightly differently. Table is always rendered
// with alignment "center" since it needs the full width. The table will handle its indentation, so now
// we don't indent the entire CaptionEditable, just the caption -- we also size the caption to be the
// size of the actual table (similar in appearance when itemWidth is set)
export type CaptionEditableContentProps =
  | {
      tableMode: true;
      content: TablePluginContent;
    }
  | {
      tableMode: false;
      content: PreviewPluginContent;
    };

interface BaseCaptionEditableProps extends MediocreSearchResultProps {
  cid: string;
  indentation: number;
  index: number;
  allowMultiline: boolean;
  children: CaptionEditableItemProps['children'];
  detachHandler: () => void;
  insertItemHandler: (offset: number) => void;
  updateHandler: (args: {text: string; formats: Format; selection?: BenchlingSelection}) => void;
  formatHandler: (args: {format: string; partition: SelectionPartition}) => void;
  moveHandler: (args: {up: boolean; beginning: boolean}) => void;
  multiSelectHandler: (args: {up: boolean}) => void;
  mergeUpHandler: (args: {anchor: boolean}) => void;
  setClipboard: (e: ElementClipboardEvent) => void;
  requestSelection?: (args: {selection: BenchlingSelection}) => void;
  tabHandler?: (args: {shiftKey: boolean; target: EventTarget}) => void;
  splitHandler: () => void;
  storeState?: (args: {selection: BenchlingSelection} | null) => void;
  restoreState?: (e: ElementKeyboardEvent, selection: {selection: BenchlingSelection}) => void;
  locked?: boolean;
  preview?: object;
  spellCheck: boolean;
  editorWidth: number;
  isLockedWithEditWarn: () => boolean;
  isSelected: boolean;
  isProtected?: boolean;
  onAtomicCut?: () => void;
}

interface State {
  itemWidth: number | undefined;
  isImageEditorOpen: boolean;
}

class CaptionEditable extends React.Component<BaseCaptionEditableProps & CaptionEditableContentProps, State> {
  static getAttachmentCid(cid: string): string {
    return `${cid}-item`;
  }

  static selectionAtStart(
    cid: string,
    content: PreviewPluginContent | TablePluginContent
  ): {selection: BenchlingSelection} {
    if (getContentAlignment(content) !== 'right') {
      // if attachment is before the text
      cid = CaptionEditable.getAttachmentCid(cid);
    }
    const start = 0;
    const end = 0;
    return {selection: {cid, start, end}};
  }

  static selectionAtEnd(
    cid: string,
    content: PreviewPluginContent | TablePluginContent
  ): {selection: BenchlingSelection} {
    let end = content.text.length;
    let start = end;
    if (getContentAlignment(content) === 'right') {
      // if attachment is after the text
      cid = CaptionEditable.getAttachmentCid(cid);
      start = 0;
      end = 0;
    }
    return {selection: {cid, start, end}};
  }

  static defaultProps = {
    indentation: 0,
    tableMode: false,
  };

  override state: State = {
    itemWidth: getContentItemWidth(this.props.content),
    isImageEditorOpen: false,
  };

  override UNSAFE_componentWillReceiveProps(
    nextProps: BaseCaptionEditableProps & CaptionEditableContentProps
  ): void {
    if (
      getContentItemWidth(this.props.content) !== getContentItemWidth(nextProps.content) ||
      this.props.isSelected !== nextProps.isSelected
    ) {
      this.setState({itemWidth: getContentItemWidth(nextProps.content)});
    }
  }

  getAttachmentCid(): string {
    return CaptionEditable.getAttachmentCid(this.props.cid);
  }

  setItemWidth = (itemWidth: number): void => {
    this.setState({itemWidth: Math.floor(itemWidth)});
  };

  _selectAttachment(): void {
    if (typeof this.props.requestSelection === 'function') {
      this.props.requestSelection({selection: {cid: this.getAttachmentCid(), start: 0, end: 0}});
    }
  }

  _selectCaptionStart(): void {
    if (typeof this.props.requestSelection === 'function') {
      this.props.requestSelection({selection: {cid: this.props.cid, start: 0, end: 0}});
    }
  }

  _selectCaptionEnd(): void {
    const end = this.props.content.text.length;
    const start = end;
    if (typeof this.props.requestSelection === 'function') {
      this.props.requestSelection({selection: {cid: this.props.cid, start, end}});
    }
  }

  moveHandler = ({up, beginning}: {up: boolean; beginning: boolean}): void => {
    const alignment = getContentAlignment(this.props.content);
    if (
      (alignment === 'center' && up) || // hitting UP with attachment above the text
      (alignment === 'left' && up && !beginning) || // hitting LEFT with attachment before the text
      (alignment === 'right' && !up && beginning)
    ) {
      // hitting RIGHT with attachment afterwards.
      this._selectAttachment();
    } else {
      // Otherwise, do the default behavior
      this.props.moveHandler({up, beginning});
    }
  };

  mergeUpHandler = ({anchor}: {anchor: boolean}): void => {
    assert(!this.props.locked, 'CaptionEditable.mergeUpHandler unexpectedly called while locked');
    const alignment = getContentAlignment(this.props.content);
    if (
      (!anchor && alignment !== 'right') || // hitting BACKSPACE with attachment before the text
      (anchor && alignment === 'right')
    ) {
      // hitting DEL with attachment after the text
      this._selectAttachment();
    } else {
      this.props.mergeUpHandler({anchor});
    }
  };

  detachHandler(): void {
    assert(!this.props.locked, 'CaptionEditable.mergeUpHandler unexpectedly called while locked');
    this.props.storeState!(null);
    this.props.detachHandler();
  }

  handleItemClick = (e: ElementMouseEvent): void => {
    if ($(e.target).closest('.mediocre-itemSelect').length) {
      e.preventDefault();
      this._selectAttachment();
    }
  };

  _isImageEditorOpen(): boolean {
    return Boolean(this.state.isImageEditorOpen);
  }

  _setIsImageEditorOpen = (isOpen: boolean): void => {
    this.setState({isImageEditorOpen: isOpen});
  };

  itemKeyDownHandler = (e: ElementKeyboardEvent): void => {
    // Cut and copy will be handled by onCut / onCopy handlers
    if (
      keyboard.isCtrlX(e) ||
      keyboard.isCtrlC(e) ||
      // When enhanced attachments is enabled, we want to let the delete handler within ImageEditor to take
      // over and delete the newly added drawings by the user. Otherwise, we want to capture the event here
      // and handle it as usual by deleting the component.
      (keyboard.isADeleteKey(e) && this._isImageEditorOpen())
    ) {
      return;
    }

    if (keyboard.isUndoRedo(e)) {
      if (!this.props.isLockedWithEditWarn()) {
        e.preventDefault();
        if (typeof this.props.restoreState === 'function') {
          this.props.restoreState(e, {selection: selutil.getSelectionIndices()});
        }
      }
      return;
    }

    // Prevent [backspace, delete, enter, tab] below from modifying if the caption editable is locked
    if ([8, 46, 13, 9].includes(e.which) && this.props.isLockedWithEditWarn()) {
      e.preventDefault();
      return;
    }

    const alignment = getContentAlignment(this.props.content);
    switch (e.which) {
      case 8:
      case 46: // BACKSPACE, DEL
        e.preventDefault();
        this.props.storeState!({selection: selutil.getSelectionIndices()});
        this.props.detachHandler();
        // Give selection back to the caption
        if (alignment === 'right') {
          this._selectCaptionEnd();
        } else {
          this._selectCaptionStart();
        }
        break;
      case 13: {
        // ENTER
        e.preventDefault();
        const newIndex = e.shiftKey ? this.props.index - 1 : this.props.index;
        this.props.insertItemHandler(newIndex);
        break;
      }
      case 9: // TAB
        e.preventDefault();
        this.props.storeState!({selection: selutil.getSelectionIndices()});
        this.props.tabHandler!({shiftKey: e.shiftKey, target: e.target});
        break;
      case 37: // LEFT
        e.preventDefault();
        if (alignment !== 'right') {
          // attachment is after the text
          this.props.moveHandler({up: true, beginning: false});
        } else {
          this._selectCaptionEnd();
        }
        break;
      case 39: // RIGHT
        e.preventDefault();
        if (alignment !== 'right') {
          // attachment is after the text
          this._selectCaptionStart();
        } else {
          this.props.moveHandler({up: false, beginning: true});
        }
        break;
      case 38: // UP
        e.preventDefault();
        this.props.moveHandler({up: true, beginning: alignment !== 'right'});
        break;
      case 40: // DOWN
        e.preventDefault();
        if (alignment !== 'center') {
          // attachment is not above the text
          this.props.moveHandler({up: false, beginning: alignment === 'left'});
        } else {
          this._selectCaptionStart();
        }
        break;
      default:
        break;
    }
  };

  _copyHandler = (e: ElementClipboardEvent): void => {
    this.props.setClipboard(e);
    e.preventDefault();
  };

  _cutHandler = (e: ElementClipboardEvent): void => {
    e.preventDefault();
    if (this.props.isLockedWithEditWarn()) {
      return;
    }
    this.props.setClipboard(e);
    if (this.props.onAtomicCut) {
      this.props.onAtomicCut();
    } else {
      this.detachHandler();
    }
  };

  renderItem(): JSX.Element {
    return (
      <CaptionEditableItem
        handleItemClick={this.handleItemClick.bind(this)}
        itemWidth={this.state.itemWidth}
        setItemWidth={this.setItemWidth.bind(this)}
        isImageEditorOpen={this.state.isImageEditorOpen}
        setIsImageEditorOpen={this._setIsImageEditorOpen}
      >
        {this.props.children}
      </CaptionEditableItem>
    );
  }

  renderCaption({className, style}: {className: string; style: object | undefined}): JSX.Element {
    return (
      <EditableComponent
        // eslint-disable-next-line no-restricted-syntax
        {..._.extend({}, this.props, {
          key: 'caption',
          style,
          className: classNames(className, 'mediocre-captionEditable-caption'),
          cid: this.props.cid,
          text: this.props.content.text,
          formats: this.props.content.formats,
          tabHandler: this.props.tabHandler,
          mergeUpHandler: this.mergeUpHandler,
          moveHandler: this.moveHandler,
          index: this.props.index,
          allowDefaultPaste: true,
          allowRichText: true,
          allowAtSearch: true,
          allowSlashSearch: false,
          spellCheck: this.props.spellCheck,
          isProtected: this.props.isProtected,
          ...forwardSearchResultProps(this.props),
        })}
      />
    );
  }

  renderTopCell(cellAlignment: string, componentAlignment: string, isSideCaptioned: boolean): JSX.Element {
    const hasComponent = cellAlignment === componentAlignment;
    const hasSideCaption = isSideCaptioned && !hasComponent && cellAlignment !== 'center';

    const cellProps = {
      ...getTestProps({component: 'CaptionEditable', element: 'topCell', key: cellAlignment}),
      className: classNames('mediocre-captionEditable-cell', {
        'mediocre-captionEditable-component': hasComponent,
      }),
      style: {
        // If there's a custom width, ensure we're not falling out of the frame
        width: (() => {
          if (hasComponent && this.state.itemWidth) {
            return Math.min(this.state.itemWidth, this.props.editorWidth);
          }
          // Center alignment is a special case where all three columns are used,
          // left and right become 0px, and center is 100%.
          if (cellAlignment === 'center') {
            if (componentAlignment === 'center') {
              return '100%';
            } else {
              return 0;
            }
          }
          return undefined;
        })(),
      },
    };
    if (hasSideCaption) {
      return this.renderCaption(cellProps);
    } else if (hasComponent) {
      return <div {...cellProps}>{this.renderItem()}</div>;
    } else {
      return <div {...cellProps} />;
    }
  }

  renderBottomCell(cellAlignment: string, componentAlignment: string): JSX.Element {
    return (
      <div
        {...getTestProps({component: 'CaptionEditable', element: 'bottomCell', key: cellAlignment})}
        className="mediocre-captionEditable-cell"
      >
        {cellAlignment === componentAlignment
          ? this.renderCaption({
              className: 'mediocre-captionEditable-caption--bottom',
              style: this.props.tableMode
                ? {
                    maxWidth:
                      TableUtil.tableWidth(this.props.content.table) + 2 * TableConstants.TABLE_PADDING,
                    marginLeft: this.props.indentation * INDENT_SIZE,
                  }
                : undefined,
            })
          : undefined}
      </div>
    );
  }

  override render(): JSX.Element {
    const componentAlignment = this.props.tableMode ? 'center' : this.props.content.alignment || 'center';

    // Detect if the image has been resized such that the caption won't fit next to the component
    let isSideCaptioned = componentAlignment !== 'center';
    if (this.state.itemWidth && this.props.editorWidth - this.state.itemWidth < CAPTION_MIN_WIDTH) {
      isSideCaptioned = false;
    }

    return (
      <div>
        {/* We use a css focus selector on this hidden div to affect the styling of the caption editable. */}
        <div
          {...getTestProps({component: 'CaptionEditable', element: 'hiddenFocusEditable'})}
          className="hiddenFocusEditable editable"
          data-cid={this.getAttachmentCid()}
          data-index={this.props.index}
          data-subindex="item"
          data-formattable={true}
          contentEditable={true}
          onCopy={this._copyHandler}
          onCut={this._cutHandler}
          onKeyDown={this.itemKeyDownHandler}
        >
          {null}
        </div>
        <div
          {...getTestProps({component: 'CaptionEditable', element: 'root'})}
          className={classNames('mediocre-captionEditable multi-editable', {
            'is-selected': this.props.isSelected,
            'mediocre-captionEditable--table': this.props.tableMode,
          })}
        >
          <div
            className="mediocre-captionEditable-indent"
            style={
              !this.props.tableMode // the table will render the indentation
                ? {paddingLeft: this.props.indentation * INDENT_SIZE}
                : undefined
            }
          >
            <div className="mediocre-captionEditable-grid">
              <div className="mediocre-captionEditable-row">
                {this.renderTopCell('left', componentAlignment, isSideCaptioned)}
                {this.renderTopCell('center', componentAlignment, isSideCaptioned)}
                {this.renderTopCell('right', componentAlignment, isSideCaptioned)}
              </div>
              {!isSideCaptioned ? (
                <div className="mediocre-captionEditable-row">
                  {this.renderBottomCell('left', componentAlignment)}
                  {this.renderBottomCell('center', componentAlignment)}
                  {this.renderBottomCell('right', componentAlignment)}
                </div>
              ) : undefined}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default CaptionEditable;
